import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Compass, DraftingCompass, CalendarClock } from "lucide-react";
import { API, IMG } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { ListingCard } from "../components/ListingCard";
import { LeadForm } from "../components/LeadForm";
import { useLead } from "../components/LeadModal";

const LOCATIONS = ["All Locations", "Siddharth Vihar", "Wave City", "Aditya World City"];

const FloorPlanCard = ({ bhk, area, index }) => {
  const { openLead } = useLead();
  return (
    <Reveal delay={index * 0.1}>
      <div className="group border border-navy-900/10 bg-white p-6 shadow-[0_8px_30px_rgb(13,27,62,0.05)] transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:border-gold-500/50 hover:shadow-[0_24px_50px_rgb(13,27,62,0.12)]" data-testid={`floorplan-card-${index}`}>
        <svg viewBox="0 0 200 140" className="w-full bg-cream" aria-label={`${bhk} floor plan sketch`}>
          <rect x="14" y="12" width="172" height="116" fill="none" stroke="#0D1B3E" strokeWidth="3" />
          <line x1="86" y1="12" x2="86" y2="76" stroke="#0D1B3E" strokeWidth="2" />
          <line x1="14" y1="76" x2="120" y2="76" stroke="#0D1B3E" strokeWidth="2" />
          <line x1="120" y1="76" x2="120" y2="128" stroke="#0D1B3E" strokeWidth="2" />
          {bhk !== "2 BHK" && <line x1="86" y1="44" x2="186" y2="44" stroke="#0D1B3E" strokeWidth="2" />}
          {bhk === "4 BHK" && <line x1="120" y1="12" x2="120" y2="44" stroke="#0D1B3E" strokeWidth="2" />}
          <path d="M86 76 a20 20 0 0 1 20 20" fill="none" stroke="#C5A059" strokeWidth="2" />
          <text x="46" y="48" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">LIVING</text>
          <text x="136" y="30" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">BED 1</text>
          <text x="63" y="106" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">KITCHEN</text>
          <text x="153" y="106" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">BED 2</text>
          {bhk !== "2 BHK" && <text x="152" y="64" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">BED 3</text>}
          {bhk === "4 BHK" && <text x="103" y="30" textAnchor="middle" fontSize="10" fill="#0D1B3E" fontFamily="Georgia">BED 4</text>}
          <rect x="168" y="112" width="18" height="16" fill="none" stroke="#C5A059" strokeWidth="2" />
        </svg>
        <div className="mt-5 flex items-baseline justify-between">
          <h3 className="font-serif text-xl font-medium text-navy-900">{bhk} Residence</h3>
          <span className="text-[12px] uppercase tracking-[0.16em] text-gray-400">{area}</span>
        </div>
        <button
          data-testid={`floorplan-request-btn-${index}`}
          onClick={() => openLead({ title: "Request Floor Plan", project: `${bhk} Floor Plan`, intent: "brochure", interest: "Pre-Launch Investment", source: "Floor Plan Request" })}
          className="mt-4 flex w-full items-center justify-center gap-2 border border-navy-900/25 px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-[background-color,color,border-color] duration-300 hover:border-navy-900 hover:bg-navy-900 hover:text-white"
        >
          <DraftingCompass className="h-4 w-4" />
          Request Full Plan
        </button>
      </div>
    </Reveal>
  );
};

export default function Projects() {
  const [listings, setListings] = useState([]);
  const [loc, setLoc] = useState("All Locations");

  useEffect(() => {
    axios.get(`${API}/listings`, { params: { category: "prelaunch" } }).then((r) => setListings(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (loc === "All Locations" ? listings : listings.filter((l) => l.location.toLowerCase().includes(loc.toLowerCase()))),
    [listings, loc]
  );

  return (
    <div data-testid="projects-page">
      <PageHero
        overline="Pre-Launch Projects"
        title="Enter before the"
        accent="city does."
        description="Siddharth Vihar, Wave City and Aditya World City — premium upcoming towers with launch-window pricing on request."
        image={IMG.tower2}
      />

      <section className="bg-white py-24 sm:py-28" data-testid="projects-list-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading
              overline="Upcoming Towers"
              title={<>Choose your <span className="italic text-gold-600">micro-market</span></>}
            />
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-2" data-testid="project-location-filters">
                {LOCATIONS.map((l) => (
                  <button
                    key={l}
                    data-testid={`filter-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setLoc(l)}
                    className={`border px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-[background-color,color,border-color] duration-300 ${
                      loc === l
                        ? "border-navy-900 bg-navy-900 text-white"
                        : "border-navy-900/20 bg-white text-navy-900/60 hover:border-gold-500 hover:text-navy-900"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="mt-14 border border-dashed border-navy-900/20 bg-cream p-10 text-center text-sm text-gray-500" data-testid="projects-empty">
              New launches for this micro-market open shortly — register your interest below and we will call you first.
            </p>
          )}
        </div>
      </section>

      <section className="bg-cream py-24 sm:py-28" data-testid="floorplans-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            overline="Unit Configurations"
            title={<>Floor plans, <span className="italic text-gold-600">previewed</span></>}
            description="Indicative layouts across configurations. The full dimensioned plan set arrives on your WhatsApp within minutes."
          />
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
            <FloorPlanCard bhk="2 BHK" area="~1,050 sq.ft" index={0} />
            <FloorPlanCard bhk="3 BHK" area="~1,480 sq.ft" index={1} />
            <FloorPlanCard bhk="4 BHK" area="~2,150 sq.ft" index={2} />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy-900 py-24 sm:py-28" data-testid="virtual-tour-section">
        <div className="pointer-events-none absolute -left-28 bottom-0 h-80 w-80 rounded-full border-[32px] border-gold-500/10" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <SectionHeading
              dark
              overline="Virtual Site Tour"
              title={<>Walk the tower <span className="italic text-gold-400">from your sofa</span></>}
              description="Book a live video walkthrough with our advisor — drone views of the plot, sample flat tour and honest answers on possession timelines."
            />
            <Reveal delay={0.2}>
              <ul className="mt-9 space-y-5">
                {[
                  { icon: Compass, text: "Live drone + location orientation of the sector" },
                  { icon: DraftingCompass, text: "Sample flat & clubhouse video walkthrough" },
                  { icon: CalendarClock, text: "Same-day slots, 10 AM – 7 PM, seven days a week" },
                ].map((f, i) => (
                  <li key={i} className="flex items-center gap-4 text-[15px] text-white/75">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold-500/40 bg-white/5">
                      <f.icon className="h-5 w-5 text-gold-400" />
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-white/15 bg-white p-8 shadow-[0_30px_70px_rgb(8,16,39,0.5)] sm:p-10">
              <p className="mb-6 font-serif text-2xl font-medium text-navy-900">Schedule your tour</p>
              <LeadForm
                tid="virtual-tour-form"
                source="Virtual Site Tour"
                defaultInterest="Schedule Site Visit"
                submitLabel="Schedule Virtual Tour"
                showMessage
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
